import * as Sentry from '@sentry/react'
import { format } from 'date-fns'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuthStore } from '../../store/authStore'
import Sidebar from './Sidebar'
import Topbar from './Topbar'
import ErrorScreen from '../ErrorScreen'
import { NotificationProvider } from '../notifications/NotificationProvider'
import { useColors } from '../../hooks/useColors'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import { userHasPermission } from '../../lib/permissions'

const getTopbarCopy = (pathname: string) => {
  if (pathname === '/') {
    return {
      title: 'Dashboard',
      subtitle: format(new Date(), 'EEEE, dd MMMM yyyy'),
    }
  }

  if (pathname === '/orders') {
    return {
      title: 'Orders',
      subtitle: 'Manage all customer orders',
    }
  }

  if (pathname === '/live-map') {
    return {
      title: 'Live Agent Map',
      subtitle: 'Real-time positions of online agents',
    }
  }

  if (pathname.startsWith('/management')) {
    return {
      title: 'Management',
      subtitle: 'Manage agents, coupons, and services',
    }
  }

  if (pathname === '/analytics') {
    return {
      title: 'Analytics',
      subtitle: 'Revenue reports and order insights',
    }
  }

  if (pathname === '/settings') {
    return {
      title: 'Settings',
      subtitle: 'Platform configuration',
    }
  }

  if (pathname === '/broadcast') {
    return {
      title: 'Broadcast',
      subtitle: 'Send WhatsApp messages to customer groups',
    }
  }

  if (pathname === '/kyc-approval') {
    return {
      title: 'KYC Approval',
      subtitle: 'Review and approve agent KYC submissions',
    }
  }

  if (pathname === '/service-pricing') {
    return {
      title: 'Service Pricing',
      subtitle: 'Manage service catalog and pricing',
    }
  }

  if (pathname.startsWith('/b2b/billing')) {
    return {
      title: 'B2B Billing',
      subtitle: 'Bulk room-by-room entry for one client',
    }
  }

  if (pathname.startsWith('/b2b/invoices')) {
    return {
      title: 'B2B Invoices',
      subtitle: 'Consolidated bills by client and cycle',
    }
  }

  if (pathname.startsWith('/b2b/clients')) {
    return {
      title: 'B2B Clients',
      subtitle: 'PGs, hostels and societies billed in bulk',
    }
  }

  if (pathname === '/customers') {
    return {
      title: 'Customers',
      subtitle: 'Customer profiles, history, and actions',
    }
  }

  return {
    title: 'Dashboard',
    subtitle: format(new Date(), 'EEEE, dd MMMM yyyy'),
  }
}

export default function AdminLayout() {
  const location = useLocation()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const hydrateUser = useAuthStore((state) => state.hydrateUser)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false)
  // Bumped on Retry to force a fresh mount of the failed page (re-fetch).
  const [pageBoundaryKey, setPageBoundaryKey] = useState(0)
  const colors = useColors()

  useKeyboardShortcuts()

  useEffect(() => {
    if (isAuthenticated) {
      void hydrateUser()
    }
  }, [hydrateUser, isAuthenticated])

  useEffect(() => {
    const handleShowShortcuts = () => setShowShortcutsHelp(true)
    const handleClosePanel = () => setShowShortcutsHelp(false)

    document.addEventListener('show-shortcuts', handleShowShortcuts)
    document.addEventListener('close-panel', handleClosePanel)
    
    return () => {
      document.removeEventListener('show-shortcuts', handleShowShortcuts)
      document.removeEventListener('close-panel', handleClosePanel)
    }
  }, [])

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  /*
   * The topbar names the page from the URL alone, which would announce a
   * feature-gated module to someone who is not allowed to know it exists: the
   * route guard renders "Access restricted" while the chrome above it still
   * read "B2B Clients · PGs, hostels and societies billed in bulk".
   *
   * Same permission the nav and the route already use - nothing new, and it
   * lifts the moment b2b.view is granted.
   */
  const canSeeB2b = userHasPermission(user, 'b2b.view')
  const topbarCopy = getTopbarCopy(
    // Fall back to the generic copy rather than naming the module. Repeating
    // "Access restricted" here would also put a second identical <h1> above the
    // one the 403 page already renders.
    !canSeeB2b && location.pathname.startsWith('/b2b/') ? '/unknown' : location.pathname,
  )

  return (
    <div className="admin-layout" style={{ display: 'flex', height: '100vh' }}>
      {/* Route-agnostic notification feed: bell badge + sound on every page. */}
      <NotificationProvider />
      <Sidebar isOpen={sidebarOpen} onLinkClick={() => setSidebarOpen(false)} />
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'is-open visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      <main
        className="main-content"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: colors.bgSecondary,
        }}
      >
        <Topbar
          title={topbarCopy.title}
          subtitle={topbarCopy.subtitle}
          onMenuClick={() => setSidebarOpen(true)}
        />
        <div
          className="admin-content"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px 32px',
            color: colors.textPrimary,
          }}
        >
          {/* Per-route boundary: a page that throws during render shows the error
              here only - the sidebar/topbar stay alive and navigation still works.
              Keyed by pathname so switching pages clears a stale error, and by
              pageBoundaryKey so Retry re-mounts (re-navigates/re-fetches) the page. */}
          <Sentry.ErrorBoundary
            key={`${location.pathname}:${pageBoundaryKey}`}
            fallback={({ resetError }) => (
              <ErrorScreen
                inline
                title="This page hit an error"
                message="Something went wrong loading this page. The rest of the dashboard is still available. Use the menu, or retry this page."
                onRetry={() => {
                  resetError()
                  setPageBoundaryKey((k) => k + 1)
                }}
              />
            )}
          >
            <Outlet />
          </Sentry.ErrorBoundary>
        </div>
      </main>

      {/* Shortcuts Help Modal */}
      {showShortcutsHelp && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setShowShortcutsHelp(false)}
        >
          <div
            style={{
              background: colors.bgPrimary,
              color: colors.textPrimary,
              padding: '24px',
              borderRadius: '12px',
              width: '360px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>Keyboard Shortcuts</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <tbody>
                {[
                  { key: 'D', action: 'Go to Dashboard' },
                  { key: 'O', action: 'Go to Orders' },
                  { key: 'A', action: 'Go to Agents' },
                  { key: 'C', action: 'Go to Customers' },
                  { key: 'Cmd+N', action: 'Create New Order' },
                  { key: 'Cmd+K', action: 'Search' },
                  { key: 'Esc', action: 'Close Panel' },
                  { key: '?', action: 'Show This Help' },
                ].map((shortcut, i) => (
                  <tr key={i} style={{ borderBottom: i < 7 ? `1px solid ${colors.border}` : 'none' }}>
                    <td style={{ padding: '8px 0' }}>
                      <kbd style={{
                        background: colors.bgSecondary,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px',
                        padding: '2px 6px',
                        fontFamily: 'monospace',
                        fontSize: '12px'
                      }}>
                        {shortcut.key}
                      </kbd>
                    </td>
                    <td style={{ padding: '8px 0', textAlign: 'right', color: colors.textMuted }}>
                      {shortcut.action}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
